/**
 * Font selector for the theme editor. The Fonts panel reads FONT_OPTIONS to
 * build its dropdowns; the combobox UI renders each option in its actual
 * typeface. Fonts are sourced from Google Fonts, Fontshare (Indian Type
 * Foundry), or system fallback per FONT_SOURCES.
 *
 * On change, updates state and sends update-font to iframe which injects a
 * stylesheet link and updates body font-family.
 *
 * Catalog + source helpers were extracted to `shared/font-catalog.js` so the
 * inline-font picker, runtime CSS, export pipeline, and tests can reuse them
 * without depending on this UI module.
 */

import { store } from '../core/state.js';
import { reportError } from '../core/report-error.js';
import { bridge } from '../editing/bridge.js';
import {
  FONT_GROUPS,
  FONT_OPTIONS,
  FONT_SOURCES,
  RECOMMENDED_FONTS,
  buildFontshareHotlinkHref,
  buildGoogleHotlinkHref,
  buildLoaderTagsForFamily,
  genericFallbackFamily,
  getFontSource
} from '../shared/font-catalog.js';

export { FONT_GROUPS, FONT_OPTIONS, FONT_SOURCES, RECOMMENDED_FONTS };

/** Returns "sans-serif" | "serif" | "monospace" for a font name. */
export function getGenericFallback(fontName) {
  return genericFallbackFamily(fontName);
}

export function getCurrentFont() {
  const state = store.getState();
  return state ? state.font : 'Roboto';
}

/** Build the canonical source-aware bridge payload for body/heading updates. */
export function buildFontUpdatePayload(fontName, { weight } = {}) {
  const payload = {
    font: fontName,
    loader: buildLoaderTagsForFamily(fontName)
  };
  if (weight != null && weight !== '') payload.weight = String(weight);
  return payload;
}

export function updateFont(fontName) {
  const current = getCurrentFont();
  if (current === fontName) return;

  store.update('font', fontName, `Change font to ${fontName}`);
  // A real choice — from here the theme outranks the template's own faces.
  store.update('fontSource', 'user', null);
  bridge.send('update-font', buildFontUpdatePayload(fontName));
}

/** Returns the heading font, falling back to the body font if not set. */
export function getCurrentHeadingFont() {
  const state = store.getState();
  return state?.headingFont ?? getCurrentFont();
}

export function updateHeadingFont(fontName) {
  const current = getCurrentHeadingFont();
  if (current === fontName) return;

  store.update('headingFont', fontName, `Change heading font to ${fontName}`);
  // A real choice — from here the theme outranks the template's own faces.
  store.update('fontSource', 'user', null);
  bridge.send(
    'update-heading-font',
    buildFontUpdatePayload(fontName, {
      weight: getCurrentHeadingFontWeight()
    })
  );
}

/** Returns the heading font weight, defaulting to 700 (bold). */
export function getCurrentHeadingFontWeight() {
  const state = store.getState();
  return state?.headingFontWeight ?? '700';
}

export function updateHeadingFontWeight(weight) {
  const current = getCurrentHeadingFontWeight();
  if (current === weight) return;

  store.update('headingFontWeight', weight, `Change heading font weight to ${weight}`);
  const fontName = getCurrentHeadingFont();
  bridge.send('update-heading-font', buildFontUpdatePayload(fontName, { weight }));
}

// ── Loaders ─────────────────────────────────────────────────────────────────

function _fontLinkId(fontName) {
  // Slug matches `pb-font-<slug>` so a font loaded by the UI picker shares an
  // identity with the inline-font runtime CSS. See shared/font-catalog.js.
  return 'pb-ui-gfont-' + fontName.replace(/\s+/g, '-').toLowerCase();
}

// A stylesheet's `load` event does NOT mean its @font-face rules have reached
// the document's FontFaceSet. WebKit registers them a tick or two later, so a
// set read on the load event comes back EMPTY — measured across all three
// engines at 14 concurrent families: Chromium and Gecko register before the
// event, WebKit needed 1–2 polls of 25ms for 9 of the 14. Judging on that read
// reported every Google font in the picker as unavailable on Safari while it
// rendered perfectly, and stamped `aria-label="<family>, font unavailable"` on
// each option. `document.fonts.check()` is not the alternative: it answers true
// for a family that was never declared at all.
const FACE_REGISTRATION_BUDGET_MS = 500;
const FACE_REGISTRATION_POLL_MS = 25;

/** True when the document's FontFaceSet declares at least one face for a family. */
function familyIsRegistered(fontSet, fontName) {
  // No iterable set (older engines, test doubles without one) — we cannot
  // establish absence, so we must not report it.
  if (typeof fontSet?.forEach !== 'function') return true;
  const wanted = fontName.trim().toLowerCase();
  let found = false;
  fontSet.forEach((face) => {
    if (found) return;
    const family = String(face?.family || '')
      .replace(/['"]/g, '')
      .trim()
      .toLowerCase();
    if (family === wanted) found = true;
  });
  return found;
}

async function waitForRegisteredFace(fontSet, fontName) {
  const deadline = Date.now() + FACE_REGISTRATION_BUDGET_MS;
  for (;;) {
    if (familyIsRegistered(fontSet, fontName)) return true;
    if (Date.now() >= deadline) return false;
    await new Promise((resolve) => setTimeout(resolve, FACE_REGISTRATION_POLL_MS));
  }
}

const _fontLoadsByDocument = new WeakMap();

function _fontLoadMap(targetDoc) {
  let map = _fontLoadsByDocument.get(targetDoc);
  if (!map) {
    map = new Map();
    _fontLoadsByDocument.set(targetDoc, map);
  }
  return map;
}

/**
 * Ensure a single font is loaded in the given document. Idempotent. No-op
 * when the font is sourced as 'system'.
 */
export function ensureUIFontLoaded(fontName, targetDoc = document) {
  if (!fontName) return Promise.resolve(true);
  if (fontName === 'Roboto') return Promise.resolve(true); // baseline UA font; no need to fetch
  const source = getFontSource(fontName);
  if (source === 'system') return Promise.resolve(true);

  const loadMap = _fontLoadMap(targetDoc);
  if (loadMap.has(fontName)) return loadMap.get(fontName);

  const id = _fontLinkId(fontName);
  const existing = targetDoc.getElementById(id);
  if (existing?.dataset.pbFontLoad === 'loaded') return Promise.resolve(true);
  if (existing?.dataset.pbFontLoad === 'error') return Promise.resolve(false);

  const link = existing || targetDoc.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href =
    source === 'fontshare' ? buildFontshareHotlinkHref(fontName) : buildGoogleHotlinkHref(fontName);
  link.dataset.pbFontLoad = 'loading';

  const promise = new Promise((resolve) => {
    let settled = false;
    // Three distinct failures reach this path, and the console has to name the
    // real one: a request that never arrived, a stylesheet that arrived but
    // registered no matching @font-face, or a readiness check that threw.
    // Reporting them all as "stylesheet failed to load" asserts a network
    // cause that two of the three have not established.
    const finish = async (linkLoaded) => {
      if (settled) return;
      settled = true;
      let failure = linkLoaded ? null : 'request';
      const fontSet = targetDoc.fonts;
      if (linkLoaded && fontSet) {
        // Whether the family is DECLARED is the question, and set membership
        // answers it. The array `fonts.load()` resolves with does not: it is a
        // snapshot of what had been registered at call time, which is why the
        // wait above exists and why its length is no longer a verdict.
        if (!(await waitForRegisteredFace(fontSet, fontName))) {
          failure = 'no-face';
        } else if (typeof fontSet.load === 'function') {
          try {
            await fontSet.load(`16px "${fontName.replace(/"/g, '\\"')}"`);
          } catch (err) {
            failure = 'readiness';
            reportError('Font loader', err, {
              user: 'silent',
              detail: `Font face readiness check for "${fontName}" threw; treating it as unavailable.`
            });
          }
        }
      }
      const ok = !failure;
      link.dataset.pbFontLoad = ok ? 'loaded' : 'error';
      if (failure === 'request') {
        reportError('Font loader', new Error(`Could not load font "${fontName}".`), {
          user: 'silent',
          detail: `The ${source} stylesheet request for "${fontName}" failed — offline, blocked, or rejected by the host.`
        });
      } else if (failure === 'no-face') {
        reportError('Font loader', new Error(`Could not load font "${fontName}".`), {
          user: 'silent',
          detail: `The ${source} stylesheet for "${fontName}" loaded, but registered no matching @font-face.`
        });
      }
      resolve(ok);
    };
    link.addEventListener(
      'load',
      () => {
        finish(true);
      },
      { once: true }
    );
    link.addEventListener(
      'error',
      () => {
        finish(false);
      },
      { once: true }
    );
  });
  loadMap.set(fontName, promise);
  if (!existing) targetDoc.head.appendChild(link);
  return promise;
}

/** Ensure many fonts are loaded. Tolerates non-array input. */
export function ensureUIFontsLoaded(fontNames, targetDoc = document) {
  if (!Array.isArray(fontNames)) return Promise.resolve([]);
  const seen = new Set();
  const pending = [];
  for (const fontName of fontNames) {
    if (!fontName || seen.has(fontName)) continue;
    seen.add(fontName);
    pending.push(ensureUIFontLoaded(fontName, targetDoc));
  }
  return Promise.all(pending);
}

export function initFontSelector() {
  // No global listeners needed — the property panel drives the UI.
}
